import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { Role } from 'library/types';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import { useAppSelector } from 'store/hooks';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CreateRoleDialog, UpdateRoleDialog } from './components';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-premium';

const Roles = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [data, setData] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteRole, setOpenDeleteRole] = useState(false);
  const [openCreateRole, setOpenCreateRole] = useState(false);
  const [openUpdateRole, setOpenUpdateRole] = useState(false);
  const [cloneRoleId, setCloneRoleId] = useState<GridRowId | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: intl.formatMessage({ id: 'projects.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getRoles();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleCloseDeleteRole = () => setOpenDeleteRole(false);
  const handleClickOpenDeleteRole = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteRole(true);
  };

  const handleCloseCreateRole = () => {
    setOpenCreateRole(false);
    setTimeout(() => {
      setCloneRoleId(null);
    }, 200);
  };
  const handleClickOpenCreateRole = () => setOpenCreateRole(true);

  const handleClickOpenCloneRole = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setCloneRoleId(rowSelectionModel[0]);
    setOpenCreateRole(true);
  };

  const handleCloseUpdateRole = () => setOpenUpdateRole(false);
  const handleClickOpenUpdateRole = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateRole(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-role')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getValues();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-role']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateRole}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="roles.createRole" defaultMessage="Rol Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['add-role']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCloneRole}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<ContentCopyIcon />}
              color="info">
              <FormattedMessage id="roles.cloneRole" defaultMessage="Rol Kopyala" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-role']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateRole}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="roles.updateRole" defaultMessage="Rol Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-role']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteRole}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="roles.deleteRole" defaultMessage="Rol Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateRoleDialog
        open={openCreateRole}
        cloneRoleId={cloneRoleId}
        handleClose={handleCloseCreateRole}
        refresh={getValues}
      />
      <UpdateRoleDialog
        open={openUpdateRole}
        roleId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateRole}
        refresh={getValues}
      />
      <DeleteDialog
        open={openDeleteRole}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteRole}
        refresh={getValues}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="roles.deleteRole" defaultMessage="Rol Sil" />}
        subTitle={
          <FormattedMessage
            id="roles.deleteRoleRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra rol veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteRole}
        successMessage={
          <FormattedMessage
            id="roles.deleteRoleCompleted"
            defaultMessage="Seçili rolü silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Roles;
